import type { CategoryResolver } from 'lib/cdtCodeDefaults'
import { formatCurrency } from 'lib/formatCurrency'
import {
  ArrowSquareOutIcon,
  CalendarCheckIcon,
  CaretRightIcon,
  ChatCircleTextIcon,
  CheckCircleIcon,
  PhoneIcon,
  ProhibitIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'
import Button from 'app/components/Button'
import { TreatmentCategoryBadge } from 'app/components/TreatmentCategoryBadge'
import { Badge, BadgeColor } from 'app/components/ui/badge'
import { Button as UiButton } from 'app/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { useSettings } from 'app/hooks/useSettings'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { TREATMENT_LIST } from 'lib/systemLists'
import { ConversationPanel } from './ConversationPanel'
import { ReasonBadge } from './ReasonBadge'
import { SegmentedControl } from './SegmentedControl'
import { SplitBar } from './SplitBar'
import type { ProcedureGrouping, TreatmentLane } from 'app/types/treatment'
import { GROUPING_SETTING, anyPrioritized, priorityGroups, priorityLabel } from 'lib/procedureGrouping'
import { UseContactMessagesReturn } from './useContactMessages'
import { TreatmentCard, TreatmentProcedure, TreatmentStatus } from './treatmentFixture'
import {
  APPOINTMENT_HEADINGS,
  DIALOG_ORDER,
  shortDate,
  statusTotals,
  STATUS_META,
  STATUS_ORDER,
  visitWords,
} from './treatmentDisplay'
import { daysSincePlanned } from './treatmentBands'

/**
 * The paper each face is printed on. It belongs to the faces rather than to
 * the dialog: left on the dialog, the white panel stood still while its
 * contents rotated inside it.
 */
const CARD_FACE = `rounded-lg border border-default bg-surface-raised shadow-lg`

/**
 * The contact drawer's well recipe: paper on a grey ground gives the dialog's
 * sections edges without a single dividing line.
 */
const WELL = `rounded-lg border border-default bg-surface-raised p-4 shadow-sm`

function StatusChip({ status, on }: { status: TreatmentStatus; on?: string | null }) {
  const meta = STATUS_META[status]
  const when = shortDate(on)
  return <Badge color={meta.badge} label={when ? `${meta.label} ${when}` : meta.label} className="shrink-0" />
}

const GROUPING_OPTIONS: { id: ProcedureGrouping; label: string; title: string }[] = [
  { id: `status`, label: `Status`, title: `What became of each piece of work` },
  { id: `priority`, label: `Priority`, title: `What the practice ranked first, in its own words` },
]

type ProcedureGroup = { label: string; badge: BadgeColor; rows: TreatmentProcedure[] }

function procedureGroupsFor(card: TreatmentCard, grouping: ProcedureGrouping): ProcedureGroup[] {
  if (grouping === `priority`) {
    return priorityGroups(card.procedures).map((group) => ({ label: group.label, badge: `gray`, rows: group.rows }))
  }
  return DIALOG_ORDER.map((status) => ({
    label: APPOINTMENT_HEADINGS[status],
    badge: STATUS_META[status].badge,
    rows: card.procedures.filter((procedure) => procedure.status === status),
  })).filter((group) => group.rows.length > 0)
}

/** Open on mount and never remembered: keyed on the card by the caller, so folds do not carry between patients. */
function ProcedureGroupSection({
  group,
  grouping,
  categoryOf,
}: {
  group: ProcedureGroup
  grouping: ProcedureGrouping
  categoryOf: CategoryResolver
}) {
  const [open, setOpen] = useState(true)
  return (
    <div className="mt-3 first:mt-1">
      <UiButton
        variant="ghost"
        size="row"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        // Ghost clears its tint on aria-expanded (a popover-trigger rule); a fold header still hovers when open.
        className="group/heading aria-expanded:hover:bg-(--overlay-hover)"
      >
        <CaretRightIcon
          size={12}
          weight="bold"
          className={cn(
            `shrink-0 text-minimal transition-transform group-hover/heading:text-subtle`,
            open && `rotate-90`,
          )}
        />
        <span className="flex items-center gap-2 text-ui-sm font-normal text-strong">
          {group.label}
          <Badge color={group.badge} label={String(group.rows.length)} />
        </span>
      </UiButton>
      {open && (
        <div className="mt-1">
          {group.rows.map((procedure, index) => (
            <ProcedureRow
              key={`${procedure.code}-${procedure.on}-${index}`}
              procedure={procedure}
              grouping={grouping}
              categoryOf={categoryOf}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** The chip says what the heading does not: priority under a status heading, status under a priority one. */
function ProcedureRow({
  procedure,
  grouping,
  categoryOf,
}: {
  procedure: TreatmentProcedure
  grouping: ProcedureGrouping
  categoryOf: CategoryResolver
}) {
  const priority = priorityLabel(procedure.priority)
  return (
    <div className="flex items-start justify-between gap-3 border-b border-subtle py-2.5 last:border-b-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-body-sm text-strong">
          <span className="text-label-xs font-semibold uppercase tracking-wide text-strong">{procedure.code}</span>
          <TreatmentCategoryBadge category={categoryOf(procedure.code)} />
          {procedure.tooth && <span className="text-default">tooth {procedure.tooth}</span>}
          {grouping === `priority` ? (
            <StatusChip status={procedure.status} on={procedure.on} />
          ) : (
            priority && <Badge color="gray" label={priority} className="shrink-0" />
          )}
        </p>
        <p className="mt-0.5 text-body-xs leading-relaxed text-subtle">{procedure.description}</p>
      </div>
      <p className="shrink-0 text-body-sm tabular-nums text-strong">{formatCurrency(procedure.fee)}</p>
    </div>
  )
}

/**
 * Everything the practice planned for this patient, and what became of each
 * piece. A patient can carry several plans and they do not all count: one
 * marked Inactive or Rejected is money the practice has already let go.
 */
export function CaseDetail({
  card,
  stage,
  stages,
  lockedStage,
  onClose,
  onMove,
  onSchedule,
  onDecline,
  onOpenContact,
  onText,
  opening,
  texting,
  threadOpen,
  thread,
  onBackFromText,
}: {
  card: TreatmentCard | undefined
  /** The id of the lane the case sits in. */
  stage: string | undefined
  stages: TreatmentLane[]
  /** The booked stage's name when the case sits in it: shown read-only, with no stage choices. */
  lockedStage?: string
  onClose: () => void
  onMove: (laneId: string) => void
  /** Absent on the list-backed board, where staff cannot book or lose a case from here. */
  onSchedule?: () => void
  onDecline?: () => void
  onOpenContact: () => void
  onText: () => void
  opening: boolean
  texting: ContactJson | undefined
  threadOpen: boolean
  thread: UseContactMessagesReturn
  onBackFromText: () => void
}) {
  const [savedGrouping, setSavedGrouping] = useSettings<ProcedureGrouping>(GROUPING_SETTING)
  const { categoryOf } = useCdtCodeMappings()
  if (!card) return null
  const totals = statusTotals(card)
  // Same rule as the card headline: in Scheduled, the money is what the booking put on the calendar.
  const scheduled = card.treatmentStatus === TREATMENT_LIST.bookedStage
  // A card nobody ranked has nothing to group by priority: no control, and the list reads by status.
  const groupable = anyPrioritized(card.procedures)
  const grouping: ProcedureGrouping = groupable && savedGrouping === `priority` ? `priority` : `status`
  const chooseGrouping = (next: ProcedureGrouping) => bg(setSavedGrouping(GROUPING_SETTING, next))
  // The board is banded by how old the plan is, so the dialog says both the
  // date and the age. A negative age is a future date, which is a data fault
  // rather than something to phrase.
  const presented = shortDate(card.presented, true)
  const age = presented ? daysSincePlanned(card.presented) : -1
  const ago = age < 0 ? undefined : age === 0 ? `today` : age === 1 ? `yesterday` : `${age} days ago`

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* A stage, not a panel: each face carries its own paper, so the card
          itself is what turns. */}
      <DialogContent className="max-w-2xl overflow-visible border-0 bg-transparent p-0 shadow-none" showClose={false}>
        {/* The front face stays in normal flow so it sets the height; the
            thread is absolutely positioned to match it, which keeps the turn
            from resizing mid-animation. */}
        <div className="[perspective:1600px]">
          <div
            className={cn(
              `relative min-h-[26rem] transform-3d transition-transform duration-500 ease-out`,
              threadOpen && `rotate-y-180`,
            )}
          >
            <div
              className={cn(
                CARD_FACE,
                `flex flex-col gap-3 bg-surface-muted p-3 backface-hidden`,
                threadOpen && `pointer-events-none`,
              )}
              aria-hidden={threadOpen}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 z-10 rounded-sm p-1 text-subtle opacity-70 transition hover:opacity-100"
              >
                <XIcon size={16} weight="bold" />
              </button>

              <div className={WELL}>
                {/* Two baseline-aligned rows rather than two stacked columns:
                    the name and the money share a line, and so do the two
                    captions under them, which a column each cannot do when the
                    headings are different sizes. */}
                <div className="pr-7">
                  <div className="flex items-baseline justify-between gap-4">
                    <DialogHeader className="min-w-0 space-y-0 text-left">
                      <DialogTitle className="truncate">{card.name}</DialogTitle>
                    </DialogHeader>
                    <p
                      className={cn(
                        `gilroy shrink-0 text-2xl leading-none`,
                        scheduled ? `text-strong` : `text-amber-700`,
                      )}
                    >
                      {formatCurrency(scheduled ? card.scheduledValue : card.value)}
                    </p>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between gap-4">
                    <p className="min-w-0 truncate text-body-xs text-subtle">
                      {presented ? `Presented ${presented}` : `No presented date`}
                      {ago && <span className="text-minimal"> · {ago}</span>}
                    </p>
                    <p className="shrink-0 text-body-xs text-subtle">
                      {scheduled ? `scheduled treatment` : `unscheduled treatment`}
                    </p>
                  </div>
                  <ReasonBadge card={card} className="mt-1.5" />
                </div>

                <SplitBar card={card} className="mt-3" />
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  {STATUS_ORDER.map((key) =>
                    totals[key] > 0 ? (
                      <span key={key} className="inline-flex items-center gap-1.5 text-body-xs text-subtle">
                        <span className={cn(`h-2 w-2 rounded-full`, STATUS_META[key].bar)} />
                        {STATUS_META[key].label} {formatCurrency(totals[key])}
                      </span>
                    ) : null,
                  )}
                </div>
              </div>

              {/* Dragging is the quick path on a desktop; these controls are
                  the only path on a touch screen, where there is no drag. */}
              <div className={cn(WELL, `space-y-2`)}>
                {lockedStage ? (
                  <p className="text-body-xs text-subtle">
                    Stage <span className="font-medium text-strong">{lockedStage}</span> · set by the booking
                  </p>
                ) : (
                  // A tinted track, not a frame with hairline dividers: a faint
                  // 1px frame drops its horizontal edges at fractional display scales.
                  <div className="flex items-stretch gap-0.5 rounded-lg border border-default bg-surface-subtle p-0.5">
                    {stages.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => onMove(option.id)}
                        // The blurb, not the label: at a quarter of the card
                        // these truncate, and "Ready to sch..." says nothing.
                        title={option.blurb}
                        aria-pressed={option.id === stage}
                        className={cn(
                          `min-w-0 flex-1 truncate rounded-md px-2 py-1 text-center text-body-xs transition`,
                          option.id === stage
                            ? `bg-inverse font-medium text-inverse`
                            : `text-subtle hover:bg-surface/70 hover:text-strong`,
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
                {(onSchedule || onDecline) && (
                  <div className="flex items-center gap-2">
                    {onSchedule && (
                      <button
                        type="button"
                        onClick={onSchedule}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-green-200 bg-green-50 py-1.5 text-body-xs text-green-700 transition hover:border-green-300 hover:bg-green-100"
                      >
                        <CheckCircleIcon size={13} weight="fill" /> Scheduled
                      </button>
                    )}
                    {onDecline && (
                      <button
                        type="button"
                        onClick={onDecline}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-default bg-surface-muted py-1.5 text-body-xs text-subtle transition hover:border-strong hover:bg-surface-strong hover:text-strong"
                      >
                        <ProhibitIcon size={13} weight="bold" /> Lost
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className={cn(WELL, `min-h-0`)}>
                {(card.outsideVisits ?? []).length > 0 && (
                  <div className="mb-3 rounded-lg border border-primary-200 bg-primary-alpha-8 px-3 py-2">
                    <p className="flex items-center gap-1.5 text-ui-sm text-primary-700">
                      <CalendarCheckIcon size={14} weight="fill" className="shrink-0 text-primary-600" />
                      Also on the schedule
                    </p>
                    <p className="mt-0.5 text-body-xs leading-relaxed text-primary-700/75">
                      Booked but not in the treatment plan.
                    </p>
                    <div className="mt-1.5 space-y-1">
                      {(card.outsideVisits ?? []).map((visit) => (
                        <p key={`${visit.on}-${visit.name}`} className="text-body-xs leading-relaxed text-default">
                          {[shortDate(visit.on), visit.name, visitWords(visit.codes, categoryOf).join(`, `)]
                            .filter(Boolean)
                            .join(` · `)}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {card.excludedValue > 0 && (
                  // Shown, but never folded into the headline: the practice
                  // closed this plan.
                  <p className="rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-subtle">
                    A further <span className="text-strong">{formatCurrency(card.excludedValue)}</span> sits on a plan
                    the practice closed. It is listed below but left out of the total.
                  </p>
                )}

                {groupable && (
                  <div className="mb-4 flex items-center">
                    <SegmentedControl options={GROUPING_OPTIONS} value={grouping} onSelect={chooseGrouping} />
                  </div>
                )}

                <div className="soft-scrollbar -mr-2 max-h-[40vh] overflow-y-auto pr-1">
                  {procedureGroupsFor(card, grouping).map((group) => (
                    <ProcedureGroupSection
                      key={`${card.id}-${group.label}`}
                      group={group}
                      grouping={grouping}
                      categoryOf={categoryOf}
                    />
                  ))}

                  {card.historyValue > 0 && (
                    <p className="mt-3 rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-subtle">
                      <span className="text-strong">{formatCurrency(card.historyValue)}</span> of completed work with
                      the practice
                      {card.historyCount > 0 && (
                        <>
                          , and {card.historyCount} {card.historyCount === 1 ? `visit` : `visits`} in the last 18 months
                        </>
                      )}
                      .
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* The reverse face. Kept mounted once a thread has been read, so
                turning back does not show half a second of nothing. */}
            <div
              className={cn(
                CARD_FACE,
                `absolute inset-0 overflow-hidden rotate-y-180 backface-hidden`,
                !threadOpen && `pointer-events-none`,
              )}
              aria-hidden={!threadOpen}
            >
              {texting && (
                <ConversationPanel
                  contact={texting}
                  messages={thread.messages}
                  loading={thread.loading}
                  sending={thread.sending}
                  failed={thread.failed}
                  active={threadOpen}
                  framed={false}
                  onSend={thread.send}
                  onBack={onBackFromText}
                  onRetry={thread.refresh}
                />
              )}
            </div>
          </div>
        </div>

        {/* Floats under the dialog rather than inside it, and fades with the
            turn rather than vanishing. */}
        <div
          className={cn(
            `absolute top-full left-1/2 mt-3 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-default bg-surface-raised p-2 shadow-elevation-4 transition-opacity duration-300`,
            threadOpen && `pointer-events-none opacity-0`,
          )}
        >
          {card.cellPhone ? (
            <a href={`tel:${card.cellPhone}`}>
              <Button mode="secondary">
                <PhoneIcon size={16} weight="bold" className="mr-1.5" /> Call
              </Button>
            </a>
          ) : (
            <span className="px-2 text-body-xs text-minimal">No number on file</span>
          )}
          <Button mode="white" disabled={!card.cellPhone || opening} onClick={onText}>
            <ChatCircleTextIcon size={16} weight="bold" className="mr-1.5" /> Text
          </Button>
          <span className="mx-0.5 h-6 w-px bg-surface-strong" />
          <Button mode="white" disabled={opening} onClick={onOpenContact}>
            <ArrowSquareOutIcon size={16} weight="bold" className="mr-1.5" />
            {opening ? `Opening` : `Contact`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
